// common
import {
  getModule,
  VuexModule,
  Module,
  Action,
  Mutation,
} from "vuex-module-decorators";
import store from "@/store";

// interfaces
import { ICustomersState } from "@/interfaces/store/ICustomers";

// helpers
import _get from "lodash/get";
import {
  // parseConfigPosition,
  // parseCurrentViewFilters,
  parseViewsConfigs,
} from "@/app-search/helpers/app-search.helpers";
// import tableConfig from "@/app-search/tables/marketing-customers-config.json";
import { query } from "@/api/app-search/app-search";
import { postCustomer, patchCustomer } from "@/api/customer/customer";
import {
  EngineKeys,
  EngineTypes,
} from "@/app-search/services/request-app-search.service";
import _cloneDeep from "lodash/cloneDeep";
import viewConfig from "@/app-search/filters/marketing-customers-view-config.json";
import UserModule from "@/store/modules/user";
import { ICustomer } from "@/interfaces/responses/customer";
import NotificationService from "@/services/notification.service";
import { LogLevel, LogService } from "@/services/logging";
import { handleErrorAndPrepareMessage } from "@/utils/helpers/common";
import {
  completeOrder,
  createNewOrderSession,
  setCustomer,
} from "@/utils/vue-helpers/orders.helper";
import { OrderSessionState } from "@smeetz/orders";
import { BookingStep } from "@/constants/booking";
import { useOrderStore } from "@smeetz/order-store";
import { useOrderMetaStore } from "@/stores/order-meta.store";
import { saveOrderForSync } from "@/db/orders";

@Module({ dynamic: true, store, name: "Customers", namespaced: true })
class Customers extends VuexModule implements ICustomersState {
  public isLoading = true;
  public customers: any = {};
  public search = "";
  public filter: any = this.getCurrentViewFilters;
  public rows: any = [];
  public selectedCustomers: any = [];
  public editedCustomer: any = [];

  /**
   * Get customers data
   *
   * @returns {object[]}
   */
  public get getCustomersList(): any {
    return _get(this.customers, "dataModel", []);
  }

  /*
  /!**
   * Get user advanced settings
   *
   * @returns {any}
   *!/
  public get getAdvancedSettings(): any {
    return UserModule.getAdvancedSettings;
  }
*/

  /**
   * Get "otherData" of the selected group
   *
   * @returns {any}
   */
  public get getGroupOtherData(): any {
    return UserModule.getGroupOtherData;
  }

  /**
   * Get "marketing-customers" table config for view
   *
   * @returns {object}
   */
  public get getViewConfig(): any {
    const publicViewConfig = _get(
      this,
      "getGroupOtherData.tables['marketing-customers'].views",
      null
    );
    // const privateViewConfig = _get(this, "getAdvancedSettings.tables['marketing-customers'].views", null);

    const params = {
      defaultConfig: viewConfig,
      publicConfig: publicViewConfig,
      privateConfig: null,
      // privateConfig: privateViewConfig
    };

    return parseViewsConfigs(params);
  }

  /**
   * Get filters list from current (active) view
   *
   * @returns {object}
   */
  public get getCurrentViewFilters(): any {
    return _cloneDeep({
      query: "",
      options: {
        page: { size: 25, current: 1 },
        filters: {},
        // filters: parseCurrentViewFilters(this.getViewConfig, this.viewId)
        result_fields: {
          country: { raw: {} },
          email: { raw: {} },
          full_name: { raw: {} },
          first_name: { raw: {} },
          last_name: { raw: {} },
          id: { raw: {} },
          phone: { raw: {} },
          group_access: { raw: {} },
          city: { raw: {} },
          zip_code: { raw: {} },
          birth_date: { raw: {} },
          email_marketing_organiser: { raw: {} },
          title: { raw: {} },
          allergies: { raw: {} },
          address: { raw: {} },
          language: { raw: {} },
          company: { raw: {} },
        },
      },
    });
  }

  public get getSelectedCustomerId(): number | null {
    const orderStore = useOrderStore();
    const customer = this.selectedCustomers.find(
      (c: any) => c.orderId === orderStore.selectedOrder?.id
    );
    if (customer) {
      return Number(customer.id);
    } else {
      return null;
    }
  }

  public get getSelectedCustomerUuid(): string {
    const orderStore = useOrderStore();
    const customer = this.selectedCustomers.find(
      (c: any) => c.orderId === orderStore.selectedOrder?.id
    );
    return customer?.uuid || "";
  }

  public get getSelectedCustomerEmail(): string {
    const orderStore = useOrderStore();
    const customer = this.selectedCustomers.find(
      (c: any) => c.orderId === orderStore.selectedOrder?.id
    );
    if (customer) {
      return customer.email;
    } else {
      return "";
    }
  }

  public get getSelectedCustomerFirstName(): string {
    const orderStore = useOrderStore();
    const customer = this.selectedCustomers.find(
      (c: any) => c.orderId === orderStore.selectedOrder?.id
    );
    if (customer) {
      const firstName = customer.fullName
        .split(" ")
        .find((_: any, index: any) => index === 0);
      return firstName;
    } else {
      return "";
    }
  }

  public get getSelectedCustomerAddress(): string {
    const orderStore = useOrderStore();
    const customer = this.selectedCustomers.find(
      (c: any) => c.orderId === orderStore.selectedOrder?.id
    );
    if (customer) {
      const address = customer.address;
      return address;
    } else {
      return "";
    }
  }

  public get getSelectedCustomerLastName(): string {
    const orderStore = useOrderStore();
    const customer = this.selectedCustomers.find(
      (c: any) => c.orderId === orderStore.selectedOrder?.id
    );
    if (customer) {
      const lastName = customer.fullName
        .split(" ")
        .find((_: any, index: any) => index === 1);
      return lastName;
    } else {
      return "";
    }
  }

  public get getSelectedCustomerPhone(): string {
    const orderStore = useOrderStore();
    const customer = this.selectedCustomers.find(
      (c: any) => c.orderId === orderStore.selectedOrder?.id
    );
    if (customer) {
      return customer.phone || "";
    } else {
      return "";
    }
  }

  public get getSelectedCustomerZipCode(): string {
    const orderStore = useOrderStore();
    const customer = this.selectedCustomers.find(
      (c: any) => c.orderId === orderStore.selectedOrder?.id
    );
    if (customer) {
      return customer.zipCode || "";
    } else {
      return "";
    }
  }

  /**
   * Look up a customer in the search index by email and return the matching
   * record. Used to source the country as an alpha-2 code: the order
   * only carries the country name, but the index stores the code the country select needs.
   *
   * @param {object} params: the id and email of the customer to match
   * @returns {Promise<any | null>} the matching indexed customer, or null
   */
  @Action({ rawError: true })
  public async fetchIndexedCustomer(params: {
    id: string | number;
    email: string;
  }): Promise<any | null> {
    const payload = {
      query: params.email,
      options: {
        page: { size: 10, current: 1 },
        filters: {},
        result_fields: {
          id: { raw: {} },
          country: { raw: {} },
        },
      },
    };

    const customers = await query(
      EngineTypes.CustomerList,
      EngineKeys.Customer,
      payload
    );
    const list = customers?.dataModel ?? [];

    return list.find((c: any) => String(c.id) === String(params.id)) ?? null;
  }

  /**
   * Create new customer
   *
   * @param {object} payload: params for request
   * @returns {Promise<ICustomer | boolean>}
   */
  @Action({ rawError: true })
  public async postCustomer(payload: any): Promise<ICustomer | boolean> {
    const orderStore = useOrderStore();
    try {
      LogService.info(
        `Going to create attached new customer, order: ${orderStore.selectedOrder?.id}`,
        payload
      );
      const { data } = await postCustomer(payload);
      LogService.info(
        `Attached customer created, order: ${orderStore.selectedOrder?.id}`,
        data
      );
      return data.customers;
    } catch (error: any) {
      const errorDetails = handleErrorAndPrepareMessage(error);

      NotificationService.showCustomMessage(
        errorDetails.errorMessage,
        "warning"
      );
      LogService.info(
        `Error when POSTing new customer, order: ${orderStore.selectedOrder?.id}`,
        { params: { error: error, errorDetails: errorDetails } },
        LogLevel.Error
      );
      // throw error;
      return false;
    }
  }

  /**
   * Update customer
   *
   * @param {object} payload: params for request
   * @returns {Promise<ICustomer>}
   */
  @Action({ rawError: true })
  public async patchCustomer(payload: any): Promise<ICustomer> {
    return await patchCustomer(payload);
  }

  @Action
  public async setSelectedCustomers(selectedCustomers: any) {
    const orderStore = useOrderStore();

    const alreadyExistingCustomer = this.selectedCustomers.find(
      (c: any) => c.orderId === orderStore.selectedOrder?.id
    );
    // one customer per order: drop the current order's customer before adding the new one
    if (alreadyExistingCustomer) {
      this.removeSelectedCustomer(alreadyExistingCustomer);
    }
    this.SET_SELECTED_CUSTOMERS(
      this.selectedCustomers.concat(selectedCustomers)
    );

    await this.syncSelectedCustomerToOrder();
  }

  /**
   * Remove the customer assigned to the current order (if any).
   */
  @Action
  public async unassignSelectedCustomer() {
    const orderStore = useOrderStore();
    const assignedCustomer = this.selectedCustomers.find(
      (c: any) => c.orderId === orderStore.selectedOrder?.id
    );
    if (!assignedCustomer) {
      return;
    }
    this.removeSelectedCustomer(assignedCustomer);

    await this.syncSelectedCustomerToOrder();
  }

  /**
   * At the CompleteOrder step, push the assigned customer (new or edited)
   * onto the order and queue it for sync. While the active session is still
   * open the customer is set on it directly. Once it is COMPLETED, an edit on
   * it would never reach the backend: the sync is exactly-once per session
   * uid, so a re-sent session only updates its payments. The change is carried
   * through a fresh session instead: create it, set the customer, complete
   * it — skipped entirely when the assignment already matches the order.
   * Removing an assignment only updates the local selection; the order keeps
   * its customer.
   */
  @Action
  public async syncSelectedCustomerToOrder() {
    const orderStore = useOrderStore();
    const orderMetaStore = useOrderMetaStore();
    if (
      !orderStore.selectedOrder ||
      orderMetaStore.step !== BookingStep.CompleteOrder
    ) {
      return;
    }

    const assignedCustomer = this.selectedCustomers.find(
      (c: any) => c.orderId === orderStore.selectedOrder?.id
    );
    if (!assignedCustomer) {
      return;
    }

    const isSessionCompleted =
      orderStore.selectedOrder.orderSession?.state ===
      OrderSessionState.COMPLETED;

    if (!isSessionCompleted) {
      // The active session is still open — set the customer on it directly.
      try {
        setCustomer();
      } catch {
        // Journaled + surfaced by the order helper. The order is still
        // queued for sync below with the customer unchanged.
      }
    } else {
      // The backend sync is exactly-once per session uid, so a customer set on
      // an already completed session would never reach it — only a new
      // completed session can deliver the change. Skip the cycle when nothing
      // actually changed.
      const currentCustomer = orderStore.selectedOrderSummary?.customer ?? null;
      const isUnchanged =
        currentCustomer !== null &&
        currentCustomer.id === this.getSelectedCustomerUuid &&
        currentCustomer.email === this.getSelectedCustomerEmail &&
        currentCustomer.firstName === this.getSelectedCustomerFirstName &&
        currentCustomer.lastName === this.getSelectedCustomerLastName;
      if (isUnchanged) {
        return;
      }

      try {
        await createNewOrderSession();
      } catch {
        // Journaled + surfaced by the order helper. Without a fresh session
        // the change cannot be recorded — the order stays as it was.
        return;
      }

      try {
        setCustomer();
      } catch {
        // Journaled + surfaced by the order helper. The session is still
        // completed below so the order never lingers on a PENDING session —
        // completing it unchanged is a no-op for the backend.
      }

      try {
        await completeOrder();
      } catch {
        // Journaled + surfaced by the completion helper. A stray PENDING
        // session left here is handled like any unfinished order (config
        // reload cleanup, session expiry).
      }
    }

    // Write-ahead: queue the order to Dexie (idempotent upsert by order id),
    // same as the completion paths in processOrder.
    await saveOrderForSync(orderStore.selectedOrder);
  }

  @Action
  public removeSelectedCustomer(customer: any) {
    this.removeCustomer(customer.id);
  }

  @Mutation
  private SET_SELECTED_CUSTOMERS(selectedCustomers: any) {
    this.selectedCustomers = selectedCustomers;
  }

  @Mutation
  private removeCustomer(id: string) {
    const index = this.selectedCustomers.findIndex((c: any) => c.id === id);
    if (index > -1) {
      this.selectedCustomers.splice(index, 1);
    }
  }

  @Action
  public setEditedCustomers(editedCustomer: any) {
    this.SET_EDITED_CUSTOMERS(editedCustomer);
  }

  @Mutation
  private SET_EDITED_CUSTOMERS(editedCustomer: any) {
    this.editedCustomer = editedCustomer;
  }

  @Mutation restore(moduleFields: Partial<ICustomersState>) {
    const { selectedCustomers } = moduleFields;

    if (selectedCustomers) {
      this.selectedCustomers = selectedCustomers;
    }
  }
}

const CustomersModule = getModule(Customers);
export default CustomersModule;
